// src/contexts/MatchContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { db } from "../firebaseConfig";
import { collection, query, where, onSnapshot, updateDoc, doc, limit } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import foundMatchSoundFile from "../assets/matchFound.mp3";
import { useUser } from "./UserContext";

const MatchContext = createContext();

export const MatchProvider = ({ children }) => {
    const { user } = useUser();
    const [match, setMatch] = useState(null);
    const [countdown, setCountdown] = useState(null);
    const [progress, setProgress] = useState(100);
    const [isJoinButtonClicked, setIsJoinButtonClicked] = useState(false);
    const timerRef = useRef(null);
    const matchIdRef = useRef(null);
    const navigate = useNavigate();
    const audioContextRef = useRef(null);
    const foundMatchBufferRef = useRef(null);
    const soundPlayed = useRef(false);
    const playedMatchIds = useRef(new Set());
    const functions = getFunctions();
    const updatePlayerScore = httpsCallable(functions, "updatePlayerScore");
    const unsubscribeRef = useRef(null);
    
    const [matchReady, setMatchReady] = useState(false);

    // Helper function to reset match state
    const resetMatchState = () => {
        setMatch(null);
        setCountdown(null);
        setProgress(100);
        setIsJoinButtonClicked(false);
        setMatchReady(false);
        soundPlayed.current = false;
        clearInterval(timerRef.current);
    };

    // This effect handles unsubscribing from the previous listener when user changes or component unmounts
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            clearInterval(timerRef.current);
        };
    }, []);

    // This effect handles setting up the listener when user is available
    useEffect(() => {
        // Reset state when user changes
        resetMatchState();
        
        // If no user, don't set up any listeners
        if (!user) return;
        
        try {
            // Only listen for active matches to reduce data transfer
            const q = query(
                collection(db, "matches"),
                where("playerIds", "array-contains", user.uid),
                where("matchActive", "==", true),
                limit(1) // We only need the most recent/active match
            );
            
            // Store unsubscribe function in ref for clean-up
            unsubscribeRef.current = onSnapshot(q, (snapshot) => {
                try {
                    snapshot.docChanges().forEach((change) => {
                        if (change.type === "removed") {
                            resetMatchState();
                            return;
                        }
                        if (change.type === "added" || change.type === "modified") {
                            const matchData = { id: change.doc.id, ...change.doc.data() };
                            if (matchData.matchActive === false) {
                                resetMatchState();
                                return;
                            }
                            setMatch(matchData);

                            // When both players have joined, mark match as ready.
                            if (
                                matchData.player1Joined &&
                                matchData.player2Joined &&
                                matchData.matchActive
                            ) {
                                setMatchReady(true);
                                // Check if auto-navigation has already occurred by inspecting the match document.
                                if (!matchData.autoNavigated) {
                                    // Update the match document to set autoNavigated to true.
                                    updateDoc(doc(db, "matches", matchData.id), { autoNavigated: true })
                                        .then(() => {
                                            navigate(`/match/${matchData.id}`);
                                            updatePlayerScore({
                                                matchId: matchData.id,
                                                questionIndex: 0,
                                                isCorrect: false,
                                            }).catch((error) => {
                                                console.error("Error updating player score:", error);
                                            });
                                        })
                                        .catch((error) => {
                                            console.error("Error updating autoNavigated flag:", error);
                                        });
                                }
                            }
                        }
                    });
                } catch (error) {
                    console.error("Error processing match data:", error);
                }
            }, (error) => {
                console.error("Error in match listener:", error);
                // Reset match state if there's an error
                resetMatchState();
            });
        } catch (error) {
            console.error("Error setting up match listener:", error);
        }
        
        // Clean up function
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            clearInterval(timerRef.current);
        };
    }, [user, navigate]);

    // Load audio buffer
    useEffect(() => {
        const loadAudio = async () => {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const fetchAndDecode = async (url) => {
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                return audioContextRef.current.decodeAudioData(arrayBuffer);
            };
            foundMatchBufferRef.current = await fetchAndDecode(foundMatchSoundFile);
        };
        loadAudio();
    }, []);

    // Play sound effect once when match becomes active
    useEffect(() => {
        if (match && match.matchActive && match.id) {
            // Only play the sound if we haven't played it for this specific match ID
            if (!playedMatchIds.current.has(match.id)) {
                const buffer = foundMatchBufferRef.current;
                if (buffer && audioContextRef.current) {
                    const source = audioContextRef.current.createBufferSource();
                    source.buffer = buffer;
                    source.connect(audioContextRef.current.destination);
                    source.start(0);
                    // Add this match ID to the set of matches we've played the sound for
                    playedMatchIds.current.add(match.id);
                }
            }
        }
    }, [match]);

    // Join match function
    const joinMatch = async () => {
        if (!user || !match || isJoinButtonClicked) return;
        setIsJoinButtonClicked(true);
        const playerIndex = match.playerIds.indexOf(user.uid);
        if (playerIndex === -1) return;
        const matchRef = doc(db, "matches", match.id);
        const updateData = playerIndex === 0 ? { player1Joined: true } : { player2Joined: true };
        try {
            await updateDoc(matchRef, updateData);
            clearInterval(timerRef.current);
        } catch (error) {
            console.error("Error updating match join status:", error);
            setIsJoinButtonClicked(false);
        }
    };

    // Reset countdown if a new match is loaded
    useEffect(() => {
        if (match && match.id && matchIdRef.current !== match.id) {
            matchIdRef.current = match.id;
            setCountdown(10);
            setProgress(100);
        }
    }, [match]);

    useEffect(() => {
        if (match && match.matchActive && !isJoinButtonClicked) {
            const intervalTime = 100; // ms
            const totalTimeMs = 10000; // 10 seconds
            const progressDecrement = 100 / (totalTimeMs / intervalTime);
            timerRef.current = setInterval(() => {
                setCountdown((prevCountdown) => {
                    if (prevCountdown <= 0.1 || isJoinButtonClicked) {
                        clearInterval(timerRef.current);
                        return prevCountdown <= 0.1 ? 0 : prevCountdown;
                    } else {
                        setProgress((prevProgress) => prevProgress - progressDecrement);
                        return prevCountdown - intervalTime / 1000;
                    }
                });
            }, intervalTime);
            return () => clearInterval(timerRef.current);
        }
    }, [match, isJoinButtonClicked]);


    return (
        <MatchContext.Provider
            value={{ match, countdown, progress, joinMatch, isJoinButtonClicked, matchReady, resetMatchState }}
        >
            {children}
        </MatchContext.Provider>
    );
};

export const useMatch = () => useContext(MatchContext);
